import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronRight, Sparkles, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const goToTab = (tab) => {
  try { window.dispatchEvent(new CustomEvent('focus-critter:navigate', { detail: { tab } })); } catch {}
};

const goToMinnes = (subtab) => {
  try { sessionStorage.setItem('minnes-subtab', subtab); } catch {}
  goToTab('minnes');
};

function StudyShortcuts({ hp }) {
  return (
    <>
      <button onClick={() => goToMinnes('hp')} data-testid="shortcut-hogskoleprovet"
        className="group relative rounded-2xl p-3 text-left overflow-hidden transition-transform hover:scale-[1.02]"
        style={{ background: 'linear-gradient(135deg, #FFF6D6 0%, #FFE8A3 60%, #FFF0E0 100%)', border: '2px solid #F5B841', boxShadow: '0 4px 12px rgba(245,184,65,0.2)' }}>
        <div className="flex items-center gap-3">
          <div className="text-4xl">🐹</div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-widest font-black text-[#B07A10]">{hp?.joined ? `Dag ${hp.day_number}/14` : 'Plugga'}</div>
            <div className="font-['Fredoka'] text-base font-bold text-[#6B4A06] leading-tight">Högskoleprovet</div>
            <div className="text-[10px] text-[#8A6210] italic mt-0.5 truncate" data-testid="shortcut-hogskoleprovet-today">
              {hp?.joined ? `Idag: ${hp.today_part?.emoji} ${hp.today_part?.name}${hp.today_checked ? ' · klart ✓' : ''}` : 'En ny del varje dag i 14 dagar'}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-[#B07A10] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </button>
      <button onClick={() => goToMinnes('lessons')} data-testid="shortcut-minnestraning"
        className="group relative rounded-2xl p-3 text-left overflow-hidden transition-transform hover:scale-[1.02]"
        style={{ background: 'linear-gradient(135deg, #F5EEFF 0%, #FFE8F5 60%, #E8F0FF 100%)', border: '2px solid #C8B6FF', boxShadow: '0 4px 12px rgba(200,182,255,0.2)' }}>
        <div className="flex items-center gap-3">
          <div className="text-4xl">🧠</div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-widest font-black text-[#7A5A9C]">Minnesteknik</div>
            <div className="font-['Fredoka'] text-base font-bold text-[#4E4370] leading-tight">Minnesträning</div>
            <div className="text-[10px] text-[#7A5A9C] italic mt-0.5 truncate">Lektioner, minnespalats och träning</div>
          </div>
          <ChevronRight className="w-4 h-4 text-[#7A5A9C] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </button>
    </>
  );
}

// ============== SHORTCUT GRID ==============
export default function MinSidaShortcuts() {
  const [fastingState, setFastingState] = useState({ streak: 0, active: null, total: 0 });
  const [skincareState, setSkincareState] = useState({ streak: 0, morning_done: false, evening_done: false });
  const [familyPlayState, setFamilyPlayState] = useState({ recent: 0 });
  const [challengeState, setChallengeState] = useState({ active_count: 0, joined_count: 0, spotlight: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [fs, fa, sk, ft, fpr, ch] = await Promise.allSettled([
          axios.get(`${API}/fasting/stats`,  { withCredentials: true }),
          axios.get(`${API}/fasting/active`, { withCredentials: true }),
          axios.get(`${API}/skincare/streak`,{ withCredentials: true }),
          axios.get(`${API}/skincare/today`, { withCredentials: true }),
          axios.get(`${API}/family-play/played/recent`, { withCredentials: true }),
          axios.get(`${API}/challenges/list`,{ withCredentials: true }),
        ]);
        if (cancelled) return;
        setFastingState({
          streak: fs.value?.data?.streak || 0,
          total:  fs.value?.data?.total_fasts || 0,
          active: fa.value?.data?.session || null,
        });
        setSkincareState({
          streak: sk.value?.data?.streak || 0,
          morning_done: !!ft.value?.data?.morning_done,
          evening_done: !!ft.value?.data?.evening_done,
        });
        setFamilyPlayState({
          recent: (fpr.value?.data?.items || []).length,
        });
        const cs = ch.value?.data?.challenges || [];
        const active = cs.filter((c) => c.is_active_period);
        const joined = cs.filter((c) => c.joined && !c.is_completed);
        setChallengeState({
          active_count: active.length,
          joined_count: joined.length,
          spotlight: joined[0] || active[0] || cs[0] || null,
          hp: cs.find((c) => c.slug === 'hp-14') || null,
        });
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-testid="mysida-shortcuts">
      <button onClick={() => goToTab('dagblad')} data-testid="shortcut-dagblad"
        className="group sm:col-span-2 text-left p-4 transition-transform hover:scale-[1.01]"
        style={{ background: '#F4EEE1', border: '3px double #2B2620', color: '#2B2620' }}>
        <div className="text-[10px] font-black tracking-[0.35em] text-[#8C2F2F]">EXTRA EXTRA!</div>
        <div className="flex items-center gap-3">
          <div className="text-4xl">📰</div>
          <div className="flex-1 min-w-0">
            <div className="text-2xl font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Elsas Dagblad</div>
            <div className="text-xs italic opacity-80" style={{ fontFamily: "'Libre Baskerville', Georgia, serif" }}>Dagens förstasida: händelser, att göra, packlistor och kryssrutor. Bara här och nu.</div>
          </div>
          <ChevronRight className="w-5 h-5 opacity-60 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </button>
      {/* Utmaningar (mörk aesthetic som matchar modulen) */}
      <button
        onClick={() => goToTab('challenges')}
        className="group relative sm:col-span-2 rounded-2xl p-3 text-left overflow-hidden transition hover:scale-[1.01]"
        style={{
          background: 'linear-gradient(160deg, #0d1a1a 0%, #1a2a24 55%, #0d1a1a 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
        }}
        data-testid="shortcut-challenges"
      >
        <div
          className="absolute -top-10 right-0 w-[220px] h-[220px] rounded-full opacity-40 pointer-events-none"
          style={{
            background: `radial-gradient(circle, ${challengeState.spotlight?.accent || '#C8B6FF'} 0%, transparent 60%)`,
            filter: 'blur(30px)',
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="text-4xl flex-shrink-0" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }}>
            {challengeState.spotlight?.icon_emoji || '🏆'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full bg-white text-black text-[8px] font-black tracking-wider">
                {challengeState.joined_count > 0 ? 'DELTAR' : 'ÄGER RUM NU'}
              </span>
              <span className="text-[9px] uppercase tracking-widest font-black text-white/50">Utmaning</span>
            </div>
            <div className="font-['Fredoka'] text-base font-black text-white leading-tight mt-0.5">
              {challengeState.spotlight?.title || 'Utmaningar'}
            </div>
            <div className="text-[10px] text-white/60 italic mt-0.5 truncate">
              {challengeState.joined_count > 0
                ? `${challengeState.joined_count} pågår · ${challengeState.spotlight?.days_left ?? 0} dagar kvar`
                : `${challengeState.active_count || 5} utmaningar väntar`}
            </div>
            {challengeState.spotlight?.joined && (
              <div className="mt-1.5 flex items-center gap-2">
                <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${challengeState.spotlight?.percent || 0}%`,
                      background: challengeState.spotlight?.accent || '#C8B6FF',
                    }}
                  />
                </div>
                <span className="text-white/60 text-[10px] font-black tabular-nums">
                  {challengeState.spotlight?.completed_count}/{challengeState.spotlight?.duration_days}
                </span>
              </div>
            )}
          </div>
          <ChevronRight className="w-4 h-4 text-white/60 flex-shrink-0 group-hover:translate-x-0.5 transition" />
        </div>
      </button>

      {/* Vägledd hypnos */}
      <div
        className="relative sm:col-span-2 rounded-2xl p-3 overflow-hidden flex items-center gap-3"
        style={{ background: 'radial-gradient(ellipse at top left, #2a1d4f 0%, #140e2b 70%)', border: '1px solid rgba(200,182,255,0.25)' }}
        data-testid="shortcut-hypnos"
      >
        <button onClick={() => goToTab('hypnos')} className="group flex-1 min-w-0 flex items-center gap-3 text-left" data-testid="shortcut-hypnos-open">
          <div className="text-4xl flex-shrink-0">🌙</div>
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-widest font-black text-white/50">Trygg plats</div>
            <div className="font-['Fredoka'] text-base font-black text-white leading-tight">Vägledd hypnos</div>
            <div className="text-[10px] text-white/60 italic mt-0.5 truncate">Gör det jobbiga roligt · få ordning · somna lugnt</div>
          </div>
          <ChevronRight className="w-4 h-4 text-white/60 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
        </button>
        <button
          onClick={() => { try { sessionStorage.setItem('hypnos-autostart', 'sos'); } catch {} goToTab('hypnos'); }}
          className="flex-shrink-0 rounded-full px-3 py-2 text-xs font-black text-[#0b1b33] bg-[#A2D2FF] hover:scale-105 transition-transform"
          data-testid="shortcut-hypnos-sos"
        >🫧 SOS</button>
      </div>

      <StudyShortcuts hp={challengeState.hp} />

      {/* Farmor Elsa */}
      <button
        onClick={() => goToTab('experts')}
        className="group relative rounded-2xl p-3 text-left overflow-hidden transition hover:scale-[1.02]"
        style={{
          background: 'linear-gradient(135deg, #FFE8E0 0%, #FFF3D9 50%, #FFE0EC 100%)',
          border: '2px solid #F5C89A',
          boxShadow: '0 4px 12px rgba(220, 150, 100, 0.15)',
        }}
        data-testid="shortcut-farmor-elsa"
      >
        <div className="flex items-center gap-3">
          <div className="text-4xl" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}>👵</div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-widest font-black text-[#B8734C]">Fråga</div>
            <div className="font-['Fredoka'] text-base font-bold text-[#7A4A2E] leading-tight">Farmor Elsa</div>
            <div className="text-[10px] text-[#9B6B4D] italic mt-0.5">Husmorstips när tanken hakar upp sig</div>
          </div>
          <ChevronRight className="w-4 h-4 text-[#B8734C] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
        </div>
      </button>

      {/* Fasta-klocka */}
      <button
        onClick={() => goToTab('fasting')}
        className="group relative rounded-2xl p-3 text-left overflow-hidden transition hover:scale-[1.02]"
        style={{
          background: fastingState.active
            ? 'linear-gradient(135deg, #FFD9C4 0%, #FFB894 50%, #FFCEB4 100%)'
            : 'linear-gradient(135deg, #E8F0FF 0%, #DCE7FF 50%, #E5DAF7 100%)',
          border: `2px solid ${fastingState.active ? '#FF9E7A' : '#B5A0D0'}`,
          boxShadow: fastingState.active
            ? '0 4px 12px rgba(255, 140, 100, 0.2)'
            : '0 4px 12px rgba(140, 120, 200, 0.15)',
        }}
        data-testid="shortcut-fasting-clock"
      >
        <div className="flex items-center gap-3">
          <div className="text-4xl relative" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}>
            {fastingState.active ? '🔥' : '⏰'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-widest font-black text-[#7A5A9C]">
              {fastingState.active ? 'Fastar just nu' : 'Fasta'}
            </div>
            <div className="font-['Fredoka'] text-base font-bold text-[#4E4370] leading-tight">
              {fastingState.streak > 0 ? `${fastingState.streak} dagar streak` : 'Fasta-klocka'}
            </div>
            <div className="text-[10px] text-[#7A5A9C] italic mt-0.5 truncate">
              {fastingState.active
                ? `${fastingState.active.preset_label} · fortsätt så du gör 🌟`
                : (fastingState.total > 0 ? `${fastingState.total} fastor genomförda 💪` : 'Starta din första fasta ✨')}
            </div>
          </div>
          {fastingState.streak > 0 && (
            <div className="flex-shrink-0 flex items-center gap-0.5 px-2 py-1 rounded-full bg-white/70 backdrop-blur">
              <span className="text-[10px]">🔥</span>
              <span className="text-xs font-black text-[#D65A2E]" data-testid="fasting-streak-badge">{fastingState.streak}</span>
            </div>
          )}
          <ChevronRight className="w-4 h-4 text-[#7A5A9C] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
        </div>
      </button>

      {/* Hudvård */}
      <button
        onClick={() => goToTab('skincare')}
        className="group relative rounded-2xl p-3 text-left overflow-hidden transition hover:scale-[1.02]"
        style={{
          background: 'linear-gradient(135deg, #FFF0F5 0%, #FFE4EC 50%, #FDD8E4 100%)',
          border: '2px solid #F5A5C4',
          boxShadow: '0 4px 12px rgba(240, 150, 180, 0.15)',
        }}
        data-testid="shortcut-skincare"
      >
        <div className="flex items-center gap-3">
          <div className="text-4xl" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}>🧴</div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-widest font-black text-[#C24672]">Rutiner</div>
            <div className="font-['Fredoka'] text-base font-bold text-[#8B2C4E] leading-tight">
              {skincareState.streak > 0 ? `${skincareState.streak} dagar streak` : 'Hudvård'}
            </div>
            <div className="text-[10px] text-[#A5486A] italic mt-0.5 flex items-center gap-1">
              <span className={skincareState.morning_done ? 'opacity-100' : 'opacity-30'}>☀️</span>
              <span className={skincareState.evening_done ? 'opacity-100' : 'opacity-30'}>🌙</span>
              <span className="ml-1 truncate">
                {skincareState.morning_done && skincareState.evening_done ? 'båda klara idag!' :
                 skincareState.morning_done ? 'morgon klar 💗' :
                 skincareState.evening_done ? 'kväll klar 🌸' : 'checka av idag'}
              </span>
            </div>
          </div>
          {skincareState.streak > 0 && (
            <div className="flex-shrink-0 flex items-center gap-0.5 px-2 py-1 rounded-full bg-white/70 backdrop-blur">
              <span className="text-[10px]">💗</span>
              <span className="text-xs font-black text-[#C24672]" data-testid="skincare-streak-badge">{skincareState.streak}</span>
            </div>
          )}
          <ChevronRight className="w-4 h-4 text-[#C24672] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
        </div>
      </button>

      {/* Family Play */}
      <button
        onClick={() => goToTab('family-play')}
        className="group relative rounded-2xl p-3 text-left overflow-hidden transition hover:scale-[1.02]"
        style={{
          background: 'linear-gradient(135deg, #E0F5E9 0%, #D4EED9 50%, #C8E8D0 100%)',
          border: '2px solid #8FD1A0',
          boxShadow: '0 4px 12px rgba(140, 200, 160, 0.15)',
        }}
        data-testid="shortcut-family-play"
      >
        <div className="flex items-center gap-3">
          <div className="text-4xl" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}>🎨</div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-widest font-black text-[#3A8A55]">Lek-labbet</div>
            <div className="font-['Fredoka'] text-base font-bold text-[#1F5A38] leading-tight">Familj & pyssel</div>
            <div className="text-[10px] text-[#3A8A55] italic mt-0.5 truncate">
              {familyPlayState.recent > 0 ? `${familyPlayState.recent} senaste ✨` : 'Idéer + gemensamma lekar'}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-[#3A8A55] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
        </div>
      </button>
    </div>
  );
}

// ============== FARMOR QUICK ASK ==============
export function FarmorQuickAsk() {
  const [task, setTask] = useState('');
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!task.trim() || loading) return;
    setLoading(true); setError(null); setAnswer(null);
    try {
      const { data } = await axios.post(
        `${API}/experts/farmor/tips`,
        { task: task.trim(), area: 'övrigt', time_min: 15 },
        { withCredentials: true, timeout: 60000 }
      );
      setAnswer(data);
    } catch (e) {
      setError('Åh nej gumman, Farmor Elsa svarar inte just nu. Prova igen om en stund 💜');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="rounded-2xl p-3 mb-5"
      style={{
        background: 'linear-gradient(135deg, #FFF5EC 0%, #FFEEDF 100%)',
        border: '2px solid #F5C89A',
      }}
      data-testid="farmor-quick-ask"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">👵</span>
        <div>
          <div className="text-[9px] uppercase tracking-widest font-black text-[#B8734C]">Snabbfråga</div>
          <div className="font-['Fredoka'] text-sm font-bold text-[#7A4A2E] leading-tight">Farmor Elsa hjälper direkt</div>
        </div>
        <Sparkles className="w-3.5 h-3.5 text-[#F5A857] ml-auto" />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={task}
          onChange={(e) => setTask(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder='"få bort kalk i badrum" · "vika tvätt snabbt" · "middag av det jag har"'
          className="flex-1 px-3 py-2 rounded-full bg-white border-2 border-[#F5C89A] text-sm text-[#7A4A2E] placeholder:text-[#B8A090] outline-none focus:border-[#F5A857]"
          maxLength={200}
          data-testid="farmor-quick-ask-input"
          disabled={loading}
        />
        <button
          onClick={submit}
          disabled={loading || !task.trim()}
          className="flex-shrink-0 w-10 h-10 rounded-full bg-[#F5A857] text-white flex items-center justify-center disabled:opacity-50 hover:scale-105 transition"
          data-testid="farmor-quick-ask-submit"
        >
          {loading ? <span className="animate-pulse">✨</span> : <Send className="w-4 h-4" />}
        </button>
      </div>

      {loading && (
        <div className="mt-3 text-xs italic text-[#9B6B4D] flex items-center gap-2" data-testid="farmor-quick-ask-loading">
          <span className="animate-bounce">👵</span> Farmor tänker efter…
        </div>
      )}

      {error && (
        <div className="mt-3 text-xs text-[#B8734C] bg-white/60 rounded-lg p-2" data-testid="farmor-quick-ask-error">
          {error}
        </div>
      )}

      {answer && (
        <div className="mt-3 space-y-2 text-[#5C3B22] bg-white/70 rounded-xl p-3" data-testid="farmor-quick-ask-answer">
          {answer.intro && <div className="text-sm italic">{answer.intro}</div>}
          {Array.isArray(answer.materials) && answer.materials.length > 0 && (
            <div className="text-xs">
              <span className="font-black text-[#B8734C]">Du behöver: </span>
              {answer.materials.map((m) => typeof m === 'string' ? m : (m?.name || m?.title || '')).filter(Boolean).join(', ')}
            </div>
          )}
          {Array.isArray(answer.steps) && answer.steps.length > 0 && (
            <ol className="text-xs space-y-1.5 pl-4 list-decimal marker:text-[#F5A857]">
              {answer.steps.map((s, i) => {
                if (typeof s === 'string') return <li key={i}>{s}</li>;
                if (!s || typeof s !== 'object') return null;
                const title = s.title || '';
                const minutes = s.minutes ? ` (${s.minutes} min)` : '';
                return (
                  <li key={i}>
                    <span className="font-bold">{title}{minutes}</span>
                    {s.tip && <div className="text-[#7A5A42]">{s.tip}</div>}
                    {s.wait_while && (
                      <div className="italic text-[#B8734C] mt-0.5">⏳ {s.wait_while}</div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
          {answer.smart_sequence && (
            <div className="text-[10px] italic text-[#9B6B4D] bg-[#FFF5EC] rounded-md p-1.5">
              ✨ {answer.smart_sequence}
            </div>
          )}
          {answer.farmors_kloka_ord && (
            <div className="text-xs italic text-[#B8734C] pt-1 border-t border-[#F5C89A]/50">
              👵💕 {answer.farmors_kloka_ord}
            </div>
          )}
          <button
            onClick={() => { setAnswer(null); setTask(''); }}
            className="text-[10px] text-[#B8734C] underline hover:no-underline"
            data-testid="farmor-quick-ask-reset"
          >
            Fråga något annat
          </button>
        </div>
      )}
    </div>
  );
}
